import { Typography } from '@mui/material';
import { alpha, Box, useTheme } from '@mui/system';
import { animated } from '@react-spring/web';
import { Group } from '@visx/group';
import { LegendItem, LegendLabel, LegendOrdinal } from '@visx/legend';
import { ParentSize } from '@visx/responsive';
import { Pie } from '@visx/shape';
import React from 'react';
import { AnalyticsSupportedDevices, IWidgetProps } from 'types/analytics';
import { ITheme } from 'types/theme';
import { useLegendDomain } from '../analytics/useLegendDomain';
import {
	analyticsNumericFormat,
	countAccessor,
	LEGEND_GLYPH_SIZE,
	pieSortValues,
	PIE_LABEL_MINIMUM_ANGLE
} from '../analytics/utils';

const WidgetPie: React.FC<IWidgetProps> = ({ items }) => {
	const {
		palette: {
			text: { primary: primaryTextColor }
		}
	} = useTheme<ITheme>();

	// get percentage map - each item out of the total count of all items
	const percentageMap = React.useMemo(() => {
		const total = items.reduce((acc, item) => acc + item.count, 0);
		return items.reduce((acc, item) => {
			acc[item.name] = (item.count / total) * 100;
			return acc;
		}, {} as Record<string, number>);
	}, [items]);

	const [active, setActive] = React.useState('');
	const { getColorFrequency } = useLegendDomain(
		AnalyticsSupportedDevices,
		items
	);

	return (
		<Box display="flex" p={2}>
			<Box pl={1} display="flex" flexGrow={1}>
				<LegendOrdinal scale={getColorFrequency}>
					{(labels) => (
						<div style={{ display: 'flex', flexDirection: 'column' }}>
							{labels.map((label) => (
								<Box pt={1} key={label.value as string}>
									<LegendItem>
										<svg width={LEGEND_GLYPH_SIZE} height={LEGEND_GLYPH_SIZE}>
											<rect
												fill={label.value as string}
												width={LEGEND_GLYPH_SIZE}
												height={LEGEND_GLYPH_SIZE}
											/>
										</svg>
										<LegendLabel
											align="left"
											margin="0 0 0 8px"
											data-testid="legend-label"
											style={{
												maxWidth: '120px'
											}}
										>
											<Typography
												variant="body2"
												textOverflow="ellipsis"
												overflow="hidden"
												pl={1}
												title={`${label.text}: ${analyticsNumericFormat(
													percentageMap[label.text] || 0
												)}%`}
											>
												{`${label.text}: ${analyticsNumericFormat(
													percentageMap[label.text] || 0
												)}%`}
											</Typography>
										</LegendLabel>
									</LegendItem>
								</Box>
							))}
						</div>
					)}
				</LegendOrdinal>
			</Box>
			<Box display="flex" flexGrow={2}>
				<ParentSize debounceTime={10}>
					{(parent) => (
						<svg
							style={{
								height: '100%',
								width: '100%'
							}}
						>
							<Group top={parent.height / 2} left={parent.width / 2}>
								<Pie
									data={items}
									pieValue={countAccessor}
									pieSortValues={pieSortValues}
									outerRadius={parent.height / 2.1}
									innerRadius={1}
									padAngle={0.006}
								>
									{(pie) =>
										pie.arcs.map((arc) => {
											const { name, count } = arc.data;
											const arcPath = pie.path(arc);
											const [centroidX, centroidY] = pie.path.centroid(arc);
											let arcFill = getColorFrequency(name) as string;
											if (name === active) {
												arcFill = alpha(arcFill, 0.8);
											}

											const hasSpaceForLabel =
												arc.endAngle - arc.startAngle >=
												PIE_LABEL_MINIMUM_ANGLE;
											return (
												<g
													key={`arc-${name}`}
													onMouseEnter={() => setActive(name)}
													onMouseLeave={() => setActive('')}
												>
													<title>{`${name}: ${intl.formatNumber(
														count
													)}`}</title>
													<path
														d={arcPath || undefined}
														fill={arcFill as string}
													/>
													<animated.g>
														{hasSpaceForLabel && (
															<text
																fill={primaryTextColor}
																x={centroidX || 0}
																y={centroidY || 0}
																dy=".33em"
																fontSize={9}
																textAnchor="middle"
																pointerEvents="none"
															>
																{name}
															</text>
														)}
													</animated.g>
												</g>
											);
										})
									}
								</Pie>
							</Group>
						</svg>
					)}
				</ParentSize>
			</Box>
		</Box>
	);
};

export default WidgetPie;
